// src/contexts/SettingsContext.js
import React, { createContext, useState, useEffect } from 'react';
import i18n from '../i18n';

export const SettingsContext = createContext(null);

// Storage key for localStorage
const SETTINGS_STORAGE_KEY = 'bitdogSettings';

// Available languages
export const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'zh', name: '中文' },
  { code: 'ja', name: '日本語' },
  { code: 'ru', name: 'Русский' },
  { code: 'ko', name: '한국어' },
  { code: 'ar', name: 'العربية' },
  { code: 'pt', name: 'Português' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'pl', name: 'Polski' },
];

// Default settings
const DEFAULT_SETTINGS = {
  language: 'en',
  gasOption: 'medium',
  customGasPrice: '1',
  autoConfirm: false,
  slippage: '0.5',
  deadline: '20', 
  theme: 'light'
};

export const SettingsProvider = ({ children }) => {
  // Initialize state with default values
  const [language, setLanguage] = useState(DEFAULT_SETTINGS.language);
  const [gasOption, setGasOption] = useState(DEFAULT_SETTINGS.gasOption);
  const [customGasPrice, setCustomGasPrice] = useState(DEFAULT_SETTINGS.customGasPrice);
  const [autoConfirm, setAutoConfirm] = useState(DEFAULT_SETTINGS.autoConfirm);
  const [slippage, setSlippage] = useState(DEFAULT_SETTINGS.slippage);
  const [deadline, setDeadline] = useState(DEFAULT_SETTINGS.deadline);
  const [theme, setTheme] = useState(DEFAULT_SETTINGS.theme);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load settings from localStorage on initial mount
  useEffect(() => {
    const loadSettings = () => {
      try {
        console.log("SettingsProvider: Loading settings from storage");
        const storedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
        
        if (storedSettings) {
          console.log("SettingsProvider: Found stored settings");
          const parsedSettings = JSON.parse(storedSettings);
          
          // Apply stored settings, falling back to defaults if needed
          setLanguage(parsedSettings.language || DEFAULT_SETTINGS.language);
          setGasOption(parsedSettings.gasOption || DEFAULT_SETTINGS.gasOption);
          setCustomGasPrice(parsedSettings.customGasPrice || DEFAULT_SETTINGS.customGasPrice);
          setAutoConfirm(parsedSettings.autoConfirm !== undefined ? parsedSettings.autoConfirm : DEFAULT_SETTINGS.autoConfirm);
          setSlippage(parsedSettings.slippage || DEFAULT_SETTINGS.slippage);
          setDeadline(parsedSettings.deadline || DEFAULT_SETTINGS.deadline);
          setTheme(parsedSettings.theme || DEFAULT_SETTINGS.theme);
        } else {
          console.log("SettingsProvider: No stored settings found, using defaults");
        }
      } catch (error) {
        console.error('Failed to load settings from localStorage:', error);
        // On error, settings will remain at their default values
      }
      
      setIsInitialized(true);
    };
    
    loadSettings();
  }, []);

  // Change language when language setting changes
  useEffect(() => {
    if (!isInitialized) return;
    
    // Change i18n language if it doesn't match our current setting
    if (i18n.language !== language) {
      console.log('SettingsProvider: Changing i18n language to:', language);
      i18n.changeLanguage(language).catch(err => {
        console.error('Error changing language:', err);
      });
    }
  }, [language, isInitialized]);

  // Apply theme to document when it changes
  useEffect(() => {
    if (!isInitialized) return;
    
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    
    // You could also apply different colors to the status bar on mobile
    if (theme === 'dark') {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#121212');
    } else {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#f8f9fa');
    }
  }, [theme, isInitialized]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    // Only save after the initial load is complete
    if (!isInitialized) return;
    
    try {
      const settings = {
        language,
        gasOption,
        customGasPrice,
        autoConfirm,
        slippage,
        deadline,
        theme
      };
      
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }, [language, gasOption, customGasPrice, autoConfirm, slippage, deadline, isInitialized, theme]);

  // Toggle theme function
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  // Get language name from language code
  const getLanguageName = (code) => {
    const language = AVAILABLE_LANGUAGES.find(lang => lang.code === code);
    return language ? language.name : code;
  };

  // Reset function
  const resetSettings = () => {
    setLanguage(DEFAULT_SETTINGS.language);
    setGasOption(DEFAULT_SETTINGS.gasOption);
    setCustomGasPrice(DEFAULT_SETTINGS.customGasPrice);
    setAutoConfirm(DEFAULT_SETTINGS.autoConfirm);
    setSlippage(DEFAULT_SETTINGS.slippage);
    setDeadline(DEFAULT_SETTINGS.deadline);
    setTheme(DEFAULT_SETTINGS.theme);
    
    // Change language to default
    i18n.changeLanguage(DEFAULT_SETTINGS.language);
    
    // Clear from localStorage (optional)
    localStorage.removeItem(SETTINGS_STORAGE_KEY);
  };

  return (
    <SettingsContext.Provider
      value={{
        language,
        setLanguage,
        getLanguageName,
        AVAILABLE_LANGUAGES,
        gasOption,
        setGasOption,
        customGasPrice,
        setCustomGasPrice,
        autoConfirm,
        setAutoConfirm,
        slippage,
        setSlippage,
        deadline,
        setDeadline,
        theme,
        setTheme,
        toggleTheme,
        resetSettings,
        isInitialized
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};